import type Phaser from 'phaser';

type AssetFile = { key: string; url?: string };
let completedFiles = 0;
let failedFiles = 0;
let totalFiles = 0;
let visibleSince = typeof performance === 'undefined' ? 0 : performance.now();
let finished = false;
let watchdog: number | undefined;

function element(id: string): HTMLElement | null { return document.getElementById(id); }
function display(label: string, detail: string, percent: number): void {
  const progress = Math.max(0, Math.min(100, Math.round(percent)));
  const labelEl = element('spirit-loading-label');
  const detailEl = element('spirit-loading-detail');
  const percentEl = element('spirit-loading-percent');
  const fill = element('spirit-progress-fill');
  const track = element('spirit-progress-track');
  if (labelEl) labelEl.textContent = label;
  if (detailEl) detailEl.textContent = detail;
  if (percentEl) percentEl.textContent = progress + '%';
  if (fill) fill.style.width = progress + '%';
  track?.setAttribute('aria-valuenow', String(progress));
}

export function assetDescription(file: AssetFile): { label: string; detail: string } {
  const path = (file.url ?? file.key).replace(/\\/g, '/').toLowerCase();
  let label = 'กำลังโหลดภาพประกอบ';
  if (/\/world\/|\/environment\/|map|terrain|forest|village|landscape|town/.test(path)) label = 'กำลังเตรียมแผนที่และหมู่บ้าน';
  else if (/ghost|enemy|pop-|krasue|krahang|tanee/.test(path)) label = 'กำลังปลุกเหล่าผีในตำนาน';
  else if (/character|hunter-|r15-|equipment|weapon|rig\//.test(path)) label = 'กำลังเตรียมตัวละครและอาวุธ';
  else if (/\/ui\/|\/font|icon/.test(path)) label = 'กำลังเตรียมหน้าจอและไอคอน';
  else if (/\.ogg|\.mp3|\.wav|audio|music/.test(path)) label = 'กำลังเรียกเสียงแห่งโลกวิญญาณ';
  const rawName = decodeURIComponent((file.url ?? file.key).split('/').pop()?.split('?')[0] ?? file.key);
  // Never insert untrusted file names as HTML; use only textContent.
  const detail = rawName.length > 42 ? rawName.slice(0, 39) + '…' : rawName;
  return { label, detail };
}

export function setupLoadingScreen(): void {
  visibleSince = performance.now();
  const retry = element('spirit-loading-retry') as HTMLButtonElement | null;
  if (retry) retry.addEventListener('click', () => window.location.reload(), { once: true });
  display('กำลังเปิดประตูสู่โลกผีสยาม', 'กำลังเตรียมระบบเกม...', 0);
  watchdog = window.setTimeout(() => {
    if (finished) return;
    const detail = element('spirit-loading-detail');
    if (detail) detail.textContent = 'การโหลดใช้เวลานานกว่าปกติ · ตรวจสอบอินเทอร์เน็ตได้เลย';
    if (retry) retry.hidden = false;
  }, 30000);
}

export function attachPhaserAssetProgress(loader: Phaser.Loader.LoaderPlugin): void {
  completedFiles = 0;
  failedFiles = 0;
  totalFiles = 0;
  loader.on('start', () => {
    totalFiles = loader.totalToLoad;
    display('กำลังเตรียมแผนที่และสิ่งมีชีวิต', 'กำลังตรวจรายการ Asset...', 0);
  });
  loader.on('filecomplete', (_key: string) => {
    completedFiles++;
  });
  loader.on('loaderror', (_file: AssetFile) => { failedFiles++; });
  loader.on('fileprogress', (file: AssetFile) => {
    const asset = assetDescription(file);
    const count = totalFiles > 0 ? `${Math.min(completedFiles + 1, totalFiles)} / ${totalFiles} ไฟล์` : 'กำลังโหลด';
    display(asset.label, `${count} · ${asset.detail}`, loader.progress * 100);
  });
  loader.on('progress', (fraction: number) => {
    const done = Math.max(0, Math.min(100, Math.round(fraction * 100)));
    const info = element('spirit-loading-detail')?.textContent ?? '';
    display(element('spirit-loading-label')?.textContent ?? 'กำลังโหลด Asset', info, done);
  });
  loader.on('complete', () => {
    const detail = failedFiles > 0 ? `โหลดสำเร็จ ${completedFiles} ไฟล์ · ข้ามไฟล์ที่ผิดพลาด ${failedFiles} ไฟล์` : `โหลด Asset ครบ ${completedFiles} ไฟล์`;
    display('โลกผีสยามพร้อมแล้ว!', detail, 100);
  });
}

export function finishLoadingScreen(): void {
  const overlay = element('spirit-loading');
  if (!overlay || finished) return;
  finished = true;
  if (watchdog !== undefined) window.clearTimeout(watchdog);
  display('โลกผีสยามพร้อมแล้ว!', failedFiles ? `ข้ามไฟล์ที่โหลดไม่สำเร็จ ${failedFiles} ไฟล์` : 'เข้าสู่เกมกันเลย!', 100);
  const wait = Math.max(0, 650 - (performance.now() - visibleSince));
  window.setTimeout(() => {
    // In review-only mode preserve the real completed state for visual QA.
    if (new URLSearchParams(window.location.search).get('qa') === 'loading-screen') return;
    overlay.hidden = true;
    window.dispatchEvent(new Event('phi-loading-ready'));
  }, wait + 180);
}
